/**
 * Точная копия OrderFilters из miniapp на React компонентах
 * Основан на Бот/miniapp/frontend/src/components/OrderFilters.js
 */

import React, { useCallback } from 'react'
import { useOrderFiltersState } from '../hooks/useOrderFilters'
import type { FilterCallbacks, ProcessingType, OrderFiltersState } from '../../types/filters'

interface OrderFiltersProps {
  onFiltersChange?: FilterCallbacks['onFiltersChange']
  onOrdersUpdate?: FilterCallbacks['onOrdersUpdate']
  onError?: FilterCallbacks['onError']
  className?: string
  availableProcessingTypes?: ProcessingType[] // Типы обработки из текущих заказов
  // Новые пропсы для внешнего управления состоянием
  filtersState?: OrderFiltersState
  onToggleProcessingType?: (typeValue: string) => void
  onToggleExcludedType?: (typeValue: string) => void
  onSortChange?: (sort: 'newest' | 'oldest') => void
  onUpdateFilters?: (updates: Partial<OrderFiltersState>) => void
  onResetFilters?: () => void
}

export const OrderFilters: React.FC<OrderFiltersProps> = ({
  onFiltersChange,
  onOrdersUpdate,
  onError,
  className = '',
  availableProcessingTypes,
  filtersState: externalFiltersState,
  onToggleProcessingType: externalToggleProcessingType,
  onToggleExcludedType: externalToggleExcludedType,
  onSortChange: externalSortChange,
  onUpdateFilters: externalUpdateFilters,
  onResetFilters: externalResetFilters
}) => {
  // Используем только переданные типы обработки (из текущих заказов)
  const processingTypes = availableProcessingTypes || []
  
  // Используем внешнее состояние если передано, иначе создаем собственное
  const internalFiltersHook = useOrderFiltersState({ onFiltersChange, onOrdersUpdate, onError })
  
  const filtersState = externalFiltersState || internalFiltersHook.filtersState
  const updateFilters = internalFiltersHook.updateFilters
  const toggleProcessingType = externalToggleProcessingType || internalFiltersHook.toggleProcessingType
  const toggleExcludedType = externalToggleExcludedType || internalFiltersHook.toggleExcludedType
  const resetFilters = internalFiltersHook.resetFilters
  const resetExclusions = internalFiltersHook.resetExclusions

  // Показать/скрыть панель фильтров
  const toggleExpanded = useCallback(() => {
    if (externalUpdateFilters) {
      externalUpdateFilters({ isExpanded: !filtersState.isExpanded })
    } else {
      updateFilters({ isExpanded: !filtersState.isExpanded })
    }
  }, [filtersState.isExpanded, updateFilters, externalUpdateFilters])

  // Переключение вкладок
  const switchTab = useCallback((tab: 'filters' | 'exclusions') => {
    if (externalUpdateFilters) {
      externalUpdateFilters({ activeTab: tab })
    } else {
      updateFilters({ activeTab: tab })
    }
  }, [updateFilters, externalUpdateFilters])

  // Изменение сортировки
  const handleSortChange = useCallback((sort: 'newest' | 'oldest') => {
    if (externalSortChange) {
      externalSortChange(sort)
    } else {
      updateFilters({ selectedSort: sort })
    }
  }, [externalSortChange, updateFilters])

  // Подсчет активных фильтров и исключений
  const activeFiltersCount = filtersState.selectedProcessingTypes.length + (filtersState.selectedSort !== 'newest' ? 1 : 0)
  const hasActiveFilters = activeFiltersCount > 0
  const excludedCount = filtersState.excludedTypes.length

  // Получение отфильтрованных типов (без исключенных)
  const getFilteredProcessingTypes = () => {
    return processingTypes.filter(type => 
      !filtersState.excludedTypes.includes(type.name)
    )
  }

  // Если нет типов обработки, не показываем фильтры
  if (!processingTypes.length) {
    return null
  }

  return (
    <div id="filters-container" className={className}>
      {/* Кнопка фильтров в шапке */}
      <div className="filters-header-btn">
        <button className="toggle-filters-btn" onClick={toggleExpanded}>
          🔍 Фильтры и сортировка
          {hasActiveFilters && <span className="filters-badge">{activeFiltersCount}</span>}
          {excludedCount > 0 && (
            <span className="exclusions-badge" title={`Скрыто типов: ${excludedCount}`}>
              -{excludedCount}
            </span>
          )}
          <span className={`toggle-arrow ${filtersState.isExpanded ? 'expanded' : ''}`}>▼</span>
        </button>
      </div>
      
      {/* Выдвижная панель фильтров (упрощенная без вкладок) */}
      <div className={`filters-dropdown ${filtersState.isExpanded ? 'expanded' : ''}`}>
        {/* Быстрые фильтры (основной контент) */}
        <div className="filters-content">
          {/* Типы обработки (только не исключенные) */}
          <div className="filter-section">
            <h4>Типы обработки</h4>
            {excludedCount > 0 && (
              <div className="excluded-info">
                <span className="excluded-count">Скрыто типов: {excludedCount}</span>
                <button className="show-all-btn" onClick={resetExclusions}>
                  Сбросить исключения
                </button>
              </div>
            )}
            <div className="processing-types-grid">
              {getFilteredProcessingTypes().length > 0 ? (
                getFilteredProcessingTypes().map(type => (
                  <label 
                    key={type.id} 
                    className={`processing-type-item ${
                      filtersState.selectedProcessingTypes.includes(type.name) ? 'selected' : ''
                    }`}
                  >
                    <input 
                      type="checkbox" 
                      value={type.name}
                      checked={filtersState.selectedProcessingTypes.includes(type.name)}
                      onChange={() => toggleProcessingType(type.name)}
                    />
                    <span className="checkmark"></span>
                    <span className="type-name">{type.name}</span>
                  </label>
                ))
              ) : (
                <div style={{
                  color: 'var(--tg-theme-hint-color)', 
                  fontSize: '12px', 
                  padding: '8px', 
                  textAlign: 'center'
                }}>
                  Все типы обработки скрыты в настройках
                </div>
              )}
            </div>
          </div>

          {/* Сортировка */}
          <div className="filter-section">
            <h4>Сортировка</h4>
            <div className="sort-options">
              <div className="sort-toggle-container">
                <div className={`sort-toggle-slider ${filtersState.selectedSort === 'oldest' ? 'right' : ''}`}></div>
                <div 
                  className={`sort-toggle-option ${filtersState.selectedSort === 'newest' ? 'active' : ''}`}
                  onClick={() => handleSortChange('newest')}
                >
                  Новые
                </div>
                <div 
                  className={`sort-toggle-option ${filtersState.selectedSort === 'oldest' ? 'active' : ''}`}
                  onClick={() => handleSortChange('oldest')}
                >
                  Старые
                </div>
              </div>
            </div>
          </div>

          {/* Кнопки действий */}
          <div className="filter-actions">
            <button 
              className="reset-filters-btn" 
              onClick={externalResetFilters || resetFilters}
            >
              Сбросить фильтры
            </button>
          </div>
        </div>
        
        {/* СКРЫТАЯ вкладка настроек исключений (функционал сохранен, но интерфейс скрыт) */}
        <div className="tab-content exclusions-hidden" data-tab="exclusions" style={{display: 'none'}}>
          <div className="exclusions-section">
            <h4>Настройки исключений</h4>
            <p className="exclusions-description">
              🚫 Отмеченные типы будут скрыты из всех разделов. Изменения применяются мгновенно.
            </p>
            
            {/* Используем тот же дизайн что и для типов обработки */}
            <div className="processing-types-grid exclusions-types-grid">
              {processingTypes.map(type => (
                <label 
                  key={type.id} 
                  className={`processing-type-item exclusion-type-item ${
                    filtersState.excludedTypes.includes(type.name) ? 'excluded' : ''
                  }`}
                >
                  <input 
                    type="checkbox" 
                    value={type.name}
                    checked={filtersState.excludedTypes.includes(type.name)}
                    onChange={() => toggleExcludedType(type.name)}
                  />
                  <span className="type-name">{type.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderFilters